import {
  CaretDownOutlined,
  CaretUpOutlined,
  FundViewOutlined,
} from "@ant-design/icons";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Divider,
  Empty,
  Flex,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueries } from "@tanstack/react-query";
import LineChart from "../../components/charts/apexLineChart";
import { useAuth } from "../../context/AuthContext";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import {
  opcoesDaSerie,
  opcoesDaSerieDaTroca,
  opcoesDasSeries,
  opcoesDoImpacto,
  opcoesDoImpactoDaTroca,
} from "../../hooks/useEventImpact";
import useEventsV2 from "../../hooks/useEventsV2";
import useProductSearch, {
  TAMANHO_MINIMO_DA_BUSCA,
} from "../../hooks/useProductSearch";
import { PROMO_TOOLTIPS } from "../../utils/constants";
import useApiV2 from "../../utils/useApiV2";
import CabecalhoDoEvento from "./CabecalhoDoEvento";
import CollectionDetailed from "./CollectionDetailed";
import { analiseDaV2, seriesDasConsultas } from "./analiseDaV2";
import {
  agruparEventos,
  analisePorProduto,
  corDoLift,
  filtrarPorZonas,
  filtrarTrocasPorCategoria,
  formatarPct,
  linhasDeTrocas,
  mensagemDeErro,
  normalizarTexto,
  periodoSemiaberto,
  resumoDeVariosImpactos,
  rotuloDaOrigem,
  rotuloDoEscopo,
  tagDaSituacao,
} from "./promoHelpers";

const { Text, Title } = Typography;

const SELECAO_VAZIA = {
  id: null,
  description: null,
  previous_collection: null,
  next_collection: null,
  datetime: null,
};

const OPCOES_DE_FAMILIA = [
  { value: "all", label: "Todas as famílias" },
  { value: "vigencia", label: "Vigência" },
  { value: "encarte", label: "Encarte" },
];

const formatCurrencyPtBr = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TagDeVariacao = ({ variacao, semValor = "Sem base" }) => {
  const cor = corDoLift(variacao);
  if (!cor) {
    return <Tag color="grey">{semValor}</Tag>;
  }
  return (
    <Tag
      color={cor}
      icon={
        cor === "green" ? (
          <CaretUpOutlined />
        ) : cor === "red" ? (
          <CaretDownOutlined />
        ) : null
      }>
      {formatarPct(variacao)}
    </Tag>
  );
};

const consultasDeImpacto = ({ linha, period, baseline, user, api }) => {
  if (linha.tipo === "COLECAO") {
    return [
      opcoesDoImpactoDaTroca({
        getCollectionChangeImpact: api.getCollectionChangeImpact,
        user,
        changeId: linha.collection_change_id,
        period,
        baseline,
      }),
    ];
  }
  const analisaGrupo = Boolean(linha.isGroup && linha.group_id);
  const ids =
    linha.isGroup && !linha.group_id ? linha.event_ids : [linha.event_id];
  return ids.map((eventId) =>
    opcoesDoImpacto({
      getEventImpact: api.getEventImpact,
      user,
      eventId,
      period,
      baseline,
      group: analisaGrupo,
    })
  );
};

function CelulaDeImpacto({ linha, period, baseline, campo }) {
  const { user } = useAuth();
  const api = useApiV2();

  const consultas = useQueries({
    queries: consultasDeImpacto({ linha, period, baseline, user, api }),
  });

  if (consultas.some((consulta) => consulta.isLoading)) {
    return <Spin size="small" />;
  }
  if (consultas.every((consulta) => consulta.isError)) {
    return <Tag color="grey">Sem dados</Tag>;
  }

  const resumo = resumoDeVariosImpactos(
    consultas.map((consulta) => consulta.data)
  );

  if (campo === "delta_total") {
    const delta = resumo?.delta_total;
    if (typeof delta !== "number") {
      return <Tag color="grey">Sem dados</Tag>;
    }
    return (
      <Text type={delta >= 0 ? "success" : "danger"}>
        {delta >= 0 ? "+" : ""}
        {formatCurrencyPtBr(delta)}
      </Text>
    );
  }

  if (campo === "atencao") {
    if (resumo?.atencao_motivo) {
      return (
        <Tooltip title={resumo.atencao_motivo}>
          <Tag>Não se aplica</Tag>
        </Tooltip>
      );
    }
    return (
      <TagDeVariacao variacao={resumo?.atencao_pct} semValor="Sem dados" />
    );
  }

  return <TagDeVariacao variacao={resumo?.faturamento_pct} />;
}

export default function TableEventsWrapper({
  selectedDateRange,
  zones,
  selectedCollections,
  collectionsOptions = null,
  period,
  baseline = null,
}) {
  const EVENT_MARKER_COLOR = "#4A6FA5";
  const navigate = useNavigate();

  const linkDaPromocao = (evento) =>
    `/promocoes_por_sku?evento=${evento.event_id}`;

  const [selectedRow, setSelectedRow] = useState(SELECAO_VAZIA);
  const [eventType, setEventType] = useState("general_event");
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState(null);
  const [eventoSelecionado, setEventoSelecionado] = useState(null);
  const [familia, setFamilia] = useState("all");
  const [buscaDeProduto, setBuscaDeProduto] = useState("");
  const [produtoFiltrado, setProdutoFiltrado] = useState(null);
  const [buscaEmFoco, setBuscaEmFoco] = useState(false);
  const { user } = useAuth();
  const collections = collectionsOptions;
  const isGeneralEvent = eventType === "general_event";

  const periodo = useMemo(
    () => periodoSemiaberto(selectedDateRange),
    [selectedDateRange]
  );

  const trocasV2 = useEventsV2({
    start: periodo?.start,
    end: periodo?.end,
    tipos: "COLECAO",
    enabled: !isGeneralEvent,
  });

  const eventosV2 = useEventsV2({
    start: periodo?.start,
    end: periodo?.end,
    familia,
    produto: produtoFiltrado,
    enabled: isGeneralEvent,
  });

  const buscaAtrasada = useDebouncedValue(buscaDeProduto, 300);
  const sugestoesDeProduto = useProductSearch({
    q: buscaAtrasada,
    enabled: isGeneralEvent,
  });

  const linhasV2 = useMemo(
    () => agruparEventos(filtrarPorZonas(eventosV2.data?.events, zones)),
    [eventosV2.data, zones]
  );

  const opcoesDeProduto = useMemo(
    () =>
      (sugestoesDeProduto.data?.products || []).map((produto) => ({
        value: produto.produto,
        label: (
          <Space size={8}>
            <Text code>{produto.produto}</Text>
            <Text>{produto.descricao}</Text>
          </Space>
        ),
      })),
    [sugestoesDeProduto.data]
  );

  const selectedCollectionLabels = useMemo(() => {
    if (
      !Array.isArray(selectedCollections) ||
      selectedCollections.length === 0 ||
      selectedCollections.includes("all") ||
      !Array.isArray(collections)
    ) {
      return [];
    }

    const selectedCollectionValues = new Set(
      selectedCollections.map((value) => String(value))
    );

    return collections
      .filter((collection) =>
        selectedCollectionValues.has(String(collection.value))
      )
      .map((collection) => normalizarTexto(collection.label))
      .filter(Boolean);
  }, [collections, selectedCollections]);

  const linhasDasTrocas = useMemo(
    () =>
      linhasDeTrocas(
        filtrarTrocasPorCategoria(
          filtrarPorZonas(trocasV2.data?.events, zones),
          selectedCollectionLabels
        )
      ),
    [trocasV2.data, zones, selectedCollectionLabels]
  );

  const apiV2 = useApiV2();
  const impactosDasTrocas = useQueries({
    queries: isGeneralEvent
      ? []
      : linhasDasTrocas.map((linha) =>
          opcoesDoImpactoDaTroca({
            getCollectionChangeImpact: apiV2.getCollectionChangeImpact,
            user,
            changeId: linha.collection_change_id,
            period,
            baseline,
          })
        ),
  });
  const totalDaVariacao = useMemo(() => {
    const carregando = impactosDasTrocas.some((consulta) => consulta.isLoading);
    const valor = impactosDasTrocas.reduce((soma, consulta) => {
      const delta = consulta.data?.faturamento?.delta_total;
      return soma + (typeof delta === "number" ? delta : 0);
    }, 0);
    return { carregando, valor };
  }, [impactosDasTrocas]);

  const analiseSelecionada = useMemo(() => {
    if (selectedRow.description === "collection" && selectedRow.id) {
      return { tipo: "troca", ids: [selectedRow.id], group: false };
    }
    if (selectedGroupIds) {
      const comGrupo = Boolean(eventoSelecionado?.group_id);
      return {
        tipo: "evento",
        ids: comGrupo ? [eventoSelecionado.event_id] : selectedGroupIds,
        group: comGrupo,
      };
    }
    if (selectedRow.description === "event" && selectedRow.id) {
      return { tipo: "evento", ids: [selectedRow.id], group: false };
    }
    return null;
  }, [selectedRow, selectedGroupIds, eventoSelecionado]);

  const consultasDaAnalise = useQueries({
    queries: !analiseSelecionada
      ? []
      : analiseSelecionada.tipo === "troca"
        ? [
            opcoesDoImpactoDaTroca({
              getCollectionChangeImpact: apiV2.getCollectionChangeImpact,
              user,
              changeId: analiseSelecionada.ids[0],
              period,
              baseline,
            }),
            opcoesDaSerieDaTroca({
              getCollectionChangeTimeseries:
                apiV2.getCollectionChangeTimeseries,
              user,
              changeId: analiseSelecionada.ids[0],
              period,
              baseline,
            }),
          ]
        : [
            ...analiseSelecionada.ids.map((eventId) =>
              opcoesDoImpacto({
                getEventImpact: apiV2.getEventImpact,
                user,
                eventId,
                period,
                baseline,
                group: analiseSelecionada.group,
              })
            ),
            ...(analiseSelecionada.ids.length > 1
              ? [
                  opcoesDasSeries({
                    getEventsTimeseries: apiV2.getEventsTimeseries,
                    user,
                    eventIds: analiseSelecionada.ids,
                    period,
                    baseline,
                  }),
                ]
              : [
                  opcoesDaSerie({
                    getEventTimeseries: apiV2.getEventTimeseries,
                    user,
                    eventId: analiseSelecionada.ids[0],
                    period,
                    baseline,
                    group: analiseSelecionada.group,
                  }),
                ]),
          ],
  });
  const activeIsKpiLoading = consultasDaAnalise.some(
    (consulta) => consulta.isLoading
  );
  const activeKpiData = useMemo(() => {
    if (!analiseSelecionada || activeIsKpiLoading) return undefined;
    const quantidade = analiseSelecionada.ids.length;
    const base = analiseDaV2({
      impactos: consultasDaAnalise
        .slice(0, quantidade)
        .map((consulta) => consulta.data),
      series: seriesDasConsultas(consultasDaAnalise.slice(quantidade)),
      tipo: analiseSelecionada.tipo,
      zoneDescription:
        analiseSelecionada.tipo === "evento"
          ? eventoSelecionado?.zona_nome
          : null,
    });
    if (!base) return undefined;
    return {
      ...base,
      kpi_data: {
        ...base.kpi_data,
        description: {
          ...base.kpi_data.description,
          title: eventoSelecionado?.titulo,
          comment: eventoSelecionado?.comentario,
          datetime: selectedRow.datetime ?? eventoSelecionado?.datetime,
          end_datetime:
            eventoSelecionado?.end_datetime ??
            base.kpi_data.description.end_datetime,
          previous: selectedRow.previous_collection,
          next: selectedRow.next_collection,
        },
      },
    };
  }, [
    analiseSelecionada,
    activeIsKpiLoading,
    consultasDaAnalise,
    eventoSelecionado,
    selectedRow,
  ]);

  useEffect(() => {
    setSelectedRow(SELECAO_VAZIA);
    setExpandedRowKeys([]);
    setSelectedGroupIds(null);
    setEventoSelecionado(null);
  }, [
    eventType,
    period,
    baseline,
    selectedCollections,
    selectedDateRange,
    zones,
    familia,
    produtoFiltrado,
  ]);

  const isCollectionEvent =
    activeKpiData?.kpi_data.description.event_type === "collection";

  const { revenueGraphData, permanenceGraphData, occupationGraphData } =
    useMemo(() => {
      return {
        revenueGraphData: activeKpiData?.revenue_graph_data,
        permanenceGraphData: activeKpiData?.graph_data,
        occupationGraphData: activeKpiData?.occupation_graph_data,
      };
    }, [activeKpiData]);

  const renderEventDetails = () => {
    const previousCollection =
      selectedRow?.previous_collection ||
      activeKpiData?.kpi_data.description.previous;
    const nextCollection =
      selectedRow?.next_collection || activeKpiData?.kpi_data.description.next;
    const detailDate =
      selectedRow?.datetime || activeKpiData?.kpi_data.description.datetime;

    const endDatetime = activeKpiData?.kpi_data.description.end_datetime;

    const details = [
      {
        label: isCollectionEvent ? "Troca de categoria" : "Registro de evento",
        value: null,
        isTitle: true,
      },
      {
        label: isCollectionEvent ? "Categoria anterior:" : "Título:",
        value: isCollectionEvent
          ? previousCollection
          : activeKpiData?.kpi_data.description.title,
      },
      {
        label: isCollectionEvent ? "Categoria posterior:" : "Comentário:",
        value: isCollectionEvent
          ? nextCollection
          : activeKpiData?.kpi_data.description.comment,
      },
      {
        label: isCollectionEvent ? "Data da troca: " : "Data de início: ",
        value: detailDate ? dayjs(detailDate).format("DD-MM-YYYY") : null,
      },
      ...(!isCollectionEvent && endDatetime
        ? [
            {
              label: "Data de fim: ",
              value: dayjs(endDatetime).format("DD-MM-YYYY"),
            },
          ]
        : []),
    ];

    return details.map(({ label, value, isTitle }, index) => (
      <Space key={index} align="baseline">
        <Title level={isTitle ? 3 : 4} style={{ margin: 0 }}>
          {label}
        </Title>
        {!isTitle && <Text style={{ fontSize: 20 }}>{value}</Text>}
      </Space>
    ));
  };

  const analisarTroca = (linha) => {
    setSelectedGroupIds(null);
    setEventoSelecionado(null);
    setSelectedRow({
      id: linha.collection_change_id,
      description: "collection",
      previous_collection: linha.colecao_anterior,
      next_collection: linha.colecao_nova,
      datetime: linha.datetime,
    });
  };

  const analisarEventoV2 = (linha) => {
    setEventoSelecionado(linha);
    if (analisePorProduto(linha)) {
      setSelectedRow(SELECAO_VAZIA);
      setSelectedGroupIds(null);
      return;
    }
    if (linha.isGroup) {
      setSelectedRow(SELECAO_VAZIA);
      setSelectedGroupIds(linha.event_ids);
      return;
    }
    setSelectedGroupIds(null);
    setSelectedRow({
      id: linha.event_id,
      description: "event",
      previous_collection: null,
      next_collection: null,
      datetime: linha.datetime,
    });
  };

  const colunasV2 = useMemo(
    () => [
      {
        title: "Início",
        dataIndex: "datetime",
        key: "datetime",
        width: 110,
        render: (valor) => dayjs(valor).format("DD-MM-YYYY"),
        fixed: "left",
      },
      {
        title: "Fim",
        dataIndex: "end_datetime",
        key: "end_datetime",
        width: 120,
        render: (valor, linha) => {
          const situacao = tagDaSituacao(linha.situacao);
          return (
            <Space direction="vertical" size={2}>
              <span>{valor ? dayjs(valor).format("DD-MM-YYYY") : "-"}</span>
              {situacao && linha.situacao !== "ENCERRADA" && (
                <Tag color={situacao.color}>{situacao.label}</Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: "Evento",
        dataIndex: "titulo",
        key: "titulo",
        ellipsis: {
          showTitle: false,
        },
        render: (texto) => (
          <Tooltip placement="topLeft" title={texto}>
            {texto}
          </Tooltip>
        ),
      },
      {
        title: <Tooltip title={PROMO_TOOLTIPS.escopo}>Escopo</Tooltip>,
        key: "escopo",
        width: 190,
        render: (_, linha) => {
          const escopo = rotuloDoEscopo(linha);
          return (
            <Space direction="vertical" size={2}>
              <Tag color={escopo.color}>{escopo.label}</Tag>
              {linha.escopo !== "produto" && linha.zona_nome && (
                <Tooltip placement="topLeft" title={linha.zona_nome}>
                  <Text type="secondary" ellipsis style={{ maxWidth: 170 }}>
                    {linha.zona_nome}
                  </Text>
                </Tooltip>
              )}
            </Space>
          );
        },
      },
      {
        title: "Origem",
        dataIndex: "origem",
        key: "origem",
        width: 90,
        render: (valor) => {
          const origem = rotuloDaOrigem(valor);
          return <Tag color={origem.color}>{origem.label}</Tag>;
        },
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.atencao_pct}>Tempo de atenção</Tooltip>
        ),
        key: "atencao",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="atencao"
          />
        ),
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.faturamento_pct}>Faturamento</Tooltip>
        ),
        key: "faturamento",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="faturamento"
          />
        ),
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.delta_total}>Variação R$</Tooltip>
        ),
        key: "delta_total",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="delta_total"
          />
        ),
      },
      {
        title: "Analisar",
        key: "analisar",
        width: 90,
        render: (_, linha) => (
          <Button
            icon={<FundViewOutlined />}
            type={eventoSelecionado?.key === linha.key ? "primary" : "default"}
            onClick={() => analisarEventoV2(linha)}
          />
        ),
      },
    ],
    [period, baseline, eventoSelecionado]
  );

  const colunasDeTrocas = useMemo(
    () => [
      {
        title: "Data",
        dataIndex: "data",
        key: "data",
        width: 110,
        render: (valor) => dayjs(valor).format("DD-MM-YYYY"),
        fixed: "left",
      },
      {
        title: "Zona",
        dataIndex: "zona_nome",
        key: "zona_nome",
        ellipsis: { showTitle: false },
        render: (texto) => (
          <Tooltip placement="topLeft" title={texto}>
            {texto}
          </Tooltip>
        ),
        fixed: "left",
      },
      {
        title: "Categoria anterior",
        dataIndex: "colecao_anterior",
        key: "colecao_anterior",
        ellipsis: { showTitle: false },
        render: (texto) => (
          <Tooltip placement="topLeft" title={texto}>
            {texto || "N/A"}
          </Tooltip>
        ),
      },
      {
        title: "Categoria nova",
        dataIndex: "colecao_nova",
        key: "colecao_nova",
        ellipsis: { showTitle: false },
        render: (texto) => (
          <Tooltip placement="topLeft" title={texto}>
            {texto || "N/A"}
          </Tooltip>
        ),
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.atencao_pct}>Tempo de atenção</Tooltip>
        ),
        key: "atencao",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="atencao"
          />
        ),
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.faturamento_pct}>Faturamento</Tooltip>
        ),
        key: "faturamento",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="faturamento"
          />
        ),
      },
      {
        title: (
          <Tooltip title={PROMO_TOOLTIPS.delta_total}>Variação R$</Tooltip>
        ),
        key: "delta_total",
        render: (_, linha) => (
          <CelulaDeImpacto
            linha={linha}
            period={period}
            baseline={baseline}
            campo="delta_total"
          />
        ),
      },
      {
        title: "Analisar",
        key: "analisar",
        width: 90,
        render: (_, linha) => (
          <Button
            icon={<FundViewOutlined />}
            type={
              selectedRow.id === linha.collection_change_id
                ? "primary"
                : "default"
            }
            onClick={() => analisarTroca(linha)}
          />
        ),
      },
    ],
    [period, baseline, selectedRow.id]
  );

  const renderAnaliseDeZona = (comDescricao) => (
    <>
      {comDescricao && (
        <Card style={{ width: "85%" }} loading={activeIsKpiLoading}>
          <Space direction="vertical" size="large" align="left">
            {renderEventDetails()}
          </Space>
        </Card>
      )}
      <Card
        title={
          activeKpiData?.kpi_data.description.event_type === "collection"
            ? "KPIs da troca de categoria"
            : "KPIs do evento"
        }
        align="center"
        style={{
          width: "85%",
        }}
        styles={{
          header: { backgroundColor: "var(--ouran-card-header-bg)" },
        }}
        loading={activeIsKpiLoading}>
        {activeKpiData?.kpi_data?.occupation?.before_change !== null &&
        activeKpiData?.kpi_data?.occupation?.before_change !== undefined ? (
          <CollectionDetailed kpiData={activeKpiData?.kpi_data} />
        ) : (
          <Empty />
        )}
      </Card>
      <Card
        title={`Faturamento da zona ${activeKpiData?.zone_description ?? ""}`}
        align="center"
        style={{ width: "85%" }}
        styles={{
          header: { backgroundColor: "var(--ouran-card-header-bg)" },
        }}
        loading={activeIsKpiLoading}>
        {Array.isArray(revenueGraphData?.x) &&
        Array.isArray(revenueGraphData?.y) &&
        revenueGraphData.x.length > 0 ? (
          <LineChart
            title="Faturamento (R$)"
            data={revenueGraphData}
            xAnnt={{
              x:
                revenueGraphData?.x_annotation ||
                activeKpiData?.revenue_graph_data?.x_annotation,
              text: "Início",
              strokeColor: EVENT_MARKER_COLOR,
            }}
            xAnntEnd={
              revenueGraphData?.x_annotation_end
                ? {
                    x: revenueGraphData.x_annotation_end,
                    text: "Fim",
                    strokeColor: "#E07B39",
                  }
                : null
            }
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card
        title={`Tempo de atenção da zona ${
          activeKpiData?.zone_description ?? ""
        }`}
        align="center"
        style={{ width: "85%" }}
        styles={{
          header: { backgroundColor: "var(--ouran-card-header-bg)" },
        }}
        loading={activeIsKpiLoading}>
        {Array.isArray(permanenceGraphData?.x) &&
        Array.isArray(permanenceGraphData?.y) &&
        permanenceGraphData.x.length > 0 ? (
          <LineChart
            title="Tempo de atenção (minutos)"
            data={permanenceGraphData}
            xAnnt={{
              x:
                permanenceGraphData?.x_annotation ||
                activeKpiData?.graph_data?.x_annotation,
              text: "Início",
              strokeColor: EVENT_MARKER_COLOR,
            }}
            xAnntEnd={
              permanenceGraphData?.x_annotation_end
                ? {
                    x: permanenceGraphData.x_annotation_end,
                    text: "Fim",
                    strokeColor: "#E07B39",
                  }
                : null
            }
          />
        ) : (
          <Empty />
        )}
      </Card>
      <Card
        title={`Ocupação da zona ${activeKpiData?.zone_description ?? ""}`}
        align="center"
        style={{ width: "85%" }}
        styles={{
          header: { backgroundColor: "var(--ouran-card-header-bg)" },
        }}
        loading={activeIsKpiLoading}>
        {Array.isArray(occupationGraphData?.x) &&
        occupationGraphData.x.length > 0 ? (
          <LineChart
            title="Ocupação (%)"
            data={occupationGraphData}
            xAnnt={{
              x: occupationGraphData.x_annotation,
              text: "Início",
              strokeColor: EVENT_MARKER_COLOR,
            }}
            xAnntEnd={
              occupationGraphData.x_annotation_end
                ? {
                    x: occupationGraphData.x_annotation_end,
                    text: "Fim",
                    strokeColor: "#E07B39",
                  }
                : null
            }
          />
        ) : (
          <Empty />
        )}
      </Card>
    </>
  );

  const buscaCurta =
    buscaAtrasada.trim().length < TAMANHO_MINIMO_DA_BUSCA ||
    sugestoesDeProduto.isFetching;

  const sugestoesVisiveis =
    buscaEmFoco &&
    buscaAtrasada.trim().length >= TAMANHO_MINIMO_DA_BUSCA &&
    (opcoesDeProduto.length > 0 || sugestoesDeProduto.isFetched);

  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              width: "100%",
            }}>
            <span
              style={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}>
              Mudanças registradas
            </span>
            <div style={{ marginLeft: "auto" }}>
              <Select
                placeholder="Selecione o tipo de evento"
                style={{ width: 250 }}
                value={eventType}
                onChange={setEventType}
                options={[
                  { value: "general_event", label: "Eventos gerais" },
                  {
                    value: "collection_change",
                    label: "Mudança de categoria",
                  },
                ]}
              />
            </div>
          </div>
        }
        align="center"
        style={{ width: "85%" }}
        styles={{ header: { backgroundColor: "var(--ouran-card-header-bg)" } }}>
        {isGeneralEvent && (
          <Flex
            gap={12}
            wrap="wrap"
            justify="flex-end"
            style={{ marginBottom: 12 }}>
            <AutoComplete
              style={{ width: 280 }}
              value={buscaDeProduto}
              options={opcoesDeProduto}
              allowClear
              placeholder="Filtrar por SKU (código, EAN ou descrição)"
              notFoundContent={buscaCurta ? null : "Nenhum produto encontrado"}
              open={sugestoesVisiveis}
              onFocus={() => setBuscaEmFoco(true)}
              onBlur={() => setBuscaEmFoco(false)}
              onChange={(valor) => {
                setBuscaDeProduto(valor || "");
                setBuscaEmFoco(true);
                if (!valor) setProdutoFiltrado(null);
              }}
              onSelect={(valor) => {
                setBuscaDeProduto(valor);
                setProdutoFiltrado(valor);
                setBuscaEmFoco(false);
              }}
              onKeyDown={(evento) => {
                if (evento.key === "Escape") {
                  setBuscaEmFoco(false);
                  return;
                }
                const termo = buscaDeProduto.trim();
                if (evento.key === "Enter" && termo) {
                  setProdutoFiltrado(termo);
                  setBuscaEmFoco(false);
                }
              }}
            />
            <Select
              style={{ width: 180 }}
              value={familia}
              onChange={setFamilia}
              options={OPCOES_DE_FAMILIA}
            />
          </Flex>
        )}
        {isGeneralEvent && eventosV2.isError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12, textAlign: "left" }}
            message={mensagemDeErro(
              eventosV2.error,
              "Não foi possível carregar os eventos."
            )}
          />
        )}
        {!isGeneralEvent && trocasV2.isError && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12, textAlign: "left" }}
            message={mensagemDeErro(
              trocasV2.error,
              "Não foi possível carregar as trocas de categoria."
            )}
          />
        )}
        <Table
          columns={isGeneralEvent ? colunasV2 : colunasDeTrocas}
          dataSource={isGeneralEvent ? linhasV2 : linhasDasTrocas}
          rowKey="key"
          loading={isGeneralEvent ? eventosV2.isLoading : trocasV2.isLoading}
          responsive="stack"
          align="center"
          pagination={{ pageSize: 10 }}
          scroll={{ y: 500, x: 300 }}
          childrenColumnName="_children"
          expandable={{
            expandedRowKeys,
            onExpand: (expanded, record) =>
              setExpandedRowKeys((prev) =>
                expanded
                  ? [...prev, record.key]
                  : prev.filter((k) => k !== record.key)
              ),
            rowExpandable: (record) => record.isGroup === true,
          }}
          indentSize={16}
          rowClassName={(record) => (record._isChild ? "event-child-row" : "")}
          locale={
            isGeneralEvent && produtoFiltrado
              ? { emptyText: `Nenhum evento com o SKU ${produtoFiltrado}` }
              : undefined
          }
          summary={() => {
            if (isGeneralEvent || linhasDasTrocas.length === 0) {
              return null;
            }
            const labelColSpan = Math.max(colunasDeTrocas.length - 2, 1);
            const total = totalDaVariacao.valor;

            return (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={labelColSpan}>
                  <Text strong>Total variação R$ (período)</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  {totalDaVariacao.carregando ? (
                    <Spin size="small" />
                  ) : (
                    <Text
                      strong
                      type={
                        total > 0
                          ? "success"
                          : total < 0
                            ? "danger"
                            : "secondary"
                      }>
                      {total >= 0 ? "+" : ""}
                      {formatCurrencyPtBr(total)}
                    </Text>
                  )}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} colSpan={1} />
              </Table.Summary.Row>
            );
          }}
        />
      </Card>
      {isGeneralEvent && eventoSelecionado && (
        <>
          <Divider>
            <Title level={3}>Análise detalhada</Title>
          </Divider>
          <CabecalhoDoEvento
            evento={eventoSelecionado}
            onTituloAlterado={(titulo) =>
              setEventoSelecionado((atual) =>
                atual ? { ...atual, titulo } : atual
              )
            }
          />
          {analisePorProduto(eventoSelecionado) ? (
            <Alert
              type="info"
              showIcon
              style={{ width: "85%", textAlign: "left" }}
              message="Este evento é analisado em Promoções por SKU"
              description="A quebra por produto, com faturamento, unidades e atenção de cada SKU, fica na tela Promoções por SKU."
              action={
                <Button
                  type="primary"
                  onClick={() => navigate(linkDaPromocao(eventoSelecionado))}>
                  Abrir em Promoções por SKU
                </Button>
              }
            />
          ) : (
            renderAnaliseDeZona(false)
          )}
        </>
      )}
      {!isGeneralEvent && (selectedRow.id || selectedGroupIds) && (
        <>
          <Divider>
            <Title level={3}>Análise detalhada</Title>
          </Divider>
          {renderAnaliseDeZona(true)}
        </>
      )}
    </>
  );
}
